import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import useMediaEdit, {
    CropGravity,
    EditBatch,
    EditSpec,
    FlowStep,
    GalleryFlow,
    MediaVersion,
    ProcessorInfo,
    gravityToObjectPosition,
} from '@/src/Common/components/hooks/useMediaEdit';
import { CROP_ASPECTS, describeSpec } from '@/src/Common/components/MediaBulkEdit';
import { Segmented } from '../../../components/table';
import { ConfirmSpec, errText, plural, Sheet } from './parts';

/* Bulk edit (crop or processing flow) → progress → history/rollback,
   and per-photo version history with restore. Same calls as v2. */

const GRAVITIES: CropGravity[] = ['northwest', 'north', 'northeast', 'west', 'center', 'east', 'southwest', 'south', 'southeast'];
const CROP_MIMES = ['image/jpeg', 'image/png', 'video/mp4'];
const FLOW_MIMES = ['image/jpeg', 'image/png'];
const ENVIRONMENT_NAMES: Record<number, string> = { 1: 'Text', 2: 'Cloud', 3: 'Local' };

const isRunning = (b: EditBatch) => b.status === 'pending' || b.status === 'processing';
const canRollback = (b: EditBatch) => b.status === 'completed' || b.status === 'completed_with_errors';

export function statusLabel(status: EditBatch['status']): string {
    switch (status) {
        case 'pending':
            return 'Queued';
        case 'processing':
            return 'Processing';
        case 'completed':
            return 'Completed';
        case 'completed_with_errors':
            return 'Completed with errors';
        case 'failed':
            return 'Failed';
        case 'rolled_back':
            return 'Rolled back';
        default:
            return String(status);
    }
}

function rollbackSpec(b: EditBatch, rollback: () => Promise<string | void>): ConfirmSpec {
    return {
        title: 'Roll back this edit?',
        body: (
            <p>
                Restores the previous version of {plural(b.succeededCount, 'edited item')} from “{describeSpec(b.spec)}”. Guests and anyone with a shared link see
                the earlier photos again. The edited versions stay in each photo’s history.
            </p>
        ),
        actionLabel: `Roll back ${plural(b.succeededCount, 'item')}`,
        busyLabel: 'Rolling back…',
        danger: true,
        run: rollback,
    };
}

/* ------------------------------------------------------------------ */

export function BulkEditSheet({
    activationId,
    galleryId,
    media,
    confirm,
    onClose,
    onStarted,
}: {
    activationId: number;
    galleryId: number | null;
    media: any[];
    confirm: (spec: ConfirmSpec) => void;
    onClose: () => void;
    onStarted: (batch: EditBatch) => void;
}) {
    const { createEditBatch, listGalleryFlows, listProcessors, previewEdit } = useMediaEdit();
    const [mode, setMode] = useState<'crop' | 'flow'>('crop');
    const [aspect, setAspect] = useState(1);
    const [gravity, setGravity] = useState<CropGravity>('center');
    const [flows, setFlows] = useState<GalleryFlow[] | null>(null);
    const [processors, setProcessors] = useState<ProcessorInfo[] | null>(null);
    const [flowsFailed, setFlowsFailed] = useState(false);
    const [flowId, setFlowId] = useState<number | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewBusy, setPreviewBusy] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);

    const editable = useMemo(() => media.filter((m) => (mode === 'crop' ? CROP_MIMES : FLOW_MIMES).includes(m?.mimeType ?? '')), [media, mode]);
    const skipped = media.length - editable.length;
    const samples = editable.slice(0, 4);

    const flowOptions = useMemo(() => {
        if (!flows || !processors) return null;
        const byName: Record<string, ProcessorInfo> = {};
        processors.forEach((p) => (byName[p.name] = p));
        return flows.map((flow) => {
            let steps: FlowStep[] = [];
            let parseError = false;
            try {
                steps = JSON.parse(flow.flowDefinition);
                if (!Array.isArray(steps)) throw new Error('not a list');
            } catch {
                steps = [];
                parseError = true;
            }
            const localOnly = steps.map((s) => s.name).filter((name) => !byName[name]?.environments?.includes('cloud'));
            return {
                flow,
                steps,
                label: `Flow #${flow.id} (${ENVIRONMENT_NAMES[flow.environmentId] ?? '?'})`,
                summary: steps.map((s) => s.name).join(' → '),
                incompatible: parseError || localOnly.length > 0,
                reason: parseError ? 'Definition is not valid JSON' : localOnly.length > 0 ? `Runs on-device only: ${Array.from(new Set(localOnly)).join(', ')}` : null,
            };
        });
    }, [flows, processors]);

    useEffect(() => {
        if (mode !== 'flow' || flows != null) return;
        if (galleryId == null) {
            setFlows([]);
            setProcessors([]);
            return;
        }
        listGalleryFlows(galleryId)
            .then(setFlows)
            .catch(() => {
                setFlows([]);
                setFlowsFailed(true);
            });
        listProcessors()
            .then(setProcessors)
            .catch(() => {
                setProcessors([]);
                setFlowsFailed(true);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, galleryId]);

    const selected = flowOptions?.find((o) => o.flow.id === flowId) ?? null;

    const buildSpec = (): EditSpec | null => {
        if (mode === 'crop') return { type: 'crop', aspectRatio: aspect, gravity };
        if (!selected || selected.incompatible) return null;
        return { type: 'flow', flowDefinition: selected.steps, label: selected.label };
    };

    const renderPreview = async () => {
        const spec = buildSpec();
        const sample = samples[0];
        if (!spec || !sample || previewBusy) return;
        setPreviewBusy(true);
        setPreviewUrl(null);
        setPreviewError(null);
        try {
            const { url } = await previewEdit(sample.id, spec);
            setPreviewUrl(url);
        } catch (e) {
            setPreviewError(errText(e, 'Preview failed — try again.'));
        }
        setPreviewBusy(false);
    };

    const canApply = editable.length > 0 && buildSpec() != null;

    const review = () => {
        const spec = buildSpec();
        if (!spec || editable.length === 0) return;
        const verb = mode === 'crop' ? 'Crop' : 'Process';
        confirm({
            title: `${verb} ${plural(editable.length, 'item')}?`,
            body: (
                <>
                    <p>
                        {describeSpec(spec)} on {plural(editable.length, 'item')}. Each item changes for guests and anyone with a shared link as soon as it finishes.
                    </p>
                    <p>The current files are kept as versions, so you can roll back the whole edit from Edits.</p>
                    {skipped > 0 && <p>{plural(skipped, 'unsupported item')} will be skipped.</p>}
                </>
            ),
            actionLabel: `${verb} ${plural(editable.length, 'item')}`,
            busyLabel: 'Starting…',
            run: async () => {
                try {
                    const batch = await createEditBatch(
                        activationId,
                        editable.map((m) => m.id),
                        spec,
                    );
                    onStarted(batch);
                } catch (e) {
                    return errText(e, 'Couldn’t start the edit — nothing changed. Try again.');
                }
            },
        });
    };

    return (
        <Sheet
            open
            wide
            title={`Edit ${plural(editable.length, 'item')}`}
            onClose={onClose}
            footer={
                <>
                    <button type="button" className="tb-btn" onClick={onClose}>
                        Cancel
                    </button>
                    <button type="button" className="tb-new" disabled={!canApply} onClick={review}>
                        Review edit
                    </button>
                </>
            }
        >
            <Segmented<'crop' | 'flow'>
                label="Edit type"
                value={mode}
                onChange={(v) => {
                    setMode(v);
                    setPreviewUrl(null);
                    setPreviewError(null);
                }}
                options={[
                    { value: 'crop', label: 'Crop' },
                    { value: 'flow', label: 'Processing flow' },
                ]}
            />
            <p className="med-note">
                Originals are kept as versions — every edit can be undone. Anyone viewing a shared link sees the updated photo.
                {skipped > 0 && ` ${plural(skipped, 'unsupported item')} ${mode === 'flow' ? '(flows are images only)' : '(GIFs etc.)'} will be skipped.`}
            </p>

            {mode === 'flow' && (
                <div className="med-flows">
                    {flowOptions == null && <p className="quiet-empty">Loading flows…</p>}
                    {flowsFailed && <p className="field-e">Couldn’t load every flow — close and try again.</p>}
                    {flowOptions?.length === 0 && !flowsFailed && <p className="quiet-empty">No processing flows saved for this gallery — create one under Processing first.</p>}
                    {(flowOptions ?? []).map((o) => (
                        <button
                            key={o.flow.id}
                            type="button"
                            className={flowId === o.flow.id ? 'med-flow on' : 'med-flow'}
                            disabled={o.incompatible}
                            title={o.reason ?? undefined}
                            aria-pressed={flowId === o.flow.id}
                            onClick={() => {
                                setFlowId(o.flow.id);
                                setPreviewUrl(null);
                                setPreviewError(null);
                            }}
                        >
                            <span className="nm">{o.label}</span>
                            <span className="sm">{o.summary || '(empty)'}</span>
                            {o.incompatible && <span className="bad">on-device only</span>}
                        </button>
                    ))}
                    {selected && !selected.incompatible && samples[0] && (
                        <div className="med-before">
                            <img src={samples[0].thumbnailUri ?? samples[0].originalUri} alt="Before" />
                            <span aria-hidden="true">→</span>
                            {previewUrl ? (
                                <img src={previewUrl} alt="After" />
                            ) : (
                                <button type="button" className="tb-btn sm" disabled={previewBusy} onClick={renderPreview}>
                                    {previewBusy ? 'Rendering…' : 'Render preview'}
                                </button>
                            )}
                        </div>
                    )}
                    {previewError && <p className="field-e">{previewError}</p>}
                </div>
            )}

            {mode === 'crop' && (
                <>
                    <div className="med-aspects" role="group" aria-label="Aspect ratio">
                        {CROP_ASPECTS.map((a) => {
                            const on = Math.abs(aspect - a.value) < 0.001;
                            return (
                                <button key={a.label} type="button" className={on ? 'med-chip on' : 'med-chip'} aria-pressed={on} onClick={() => setAspect(a.value)}>
                                    {a.label}
                                </button>
                            );
                        })}
                    </div>
                    <div className="med-crop">
                        <div>
                            <span className="field-l">Keep</span>
                            <div className="med-gravity" role="group" aria-label="Part of the photo to keep">
                                {GRAVITIES.map((g) => (
                                    <button
                                        key={g}
                                        type="button"
                                        title={g}
                                        aria-label={g}
                                        aria-pressed={gravity === g}
                                        className={gravity === g ? 'on' : undefined}
                                        onClick={() => setGravity(g)}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className="med-samples">
                            <span className="field-l">Preview</span>
                            <div className="med-sample-row">
                                {samples.length === 0 && <span className="quiet-empty">Nothing in the selection can be cropped.</span>}
                                {samples.map((m) => {
                                    const src = m.thumbnailUri ?? m.thumbnail_800pxUri ?? m.originalUri;
                                    return (
                                        <div key={m.id} className="med-sample">
                                            <img src={src} alt="" />
                                            <span aria-hidden="true">→</span>
                                            <span className="crop" style={{ width: Math.round(64 * aspect) }}>
                                                <img src={src} alt="" style={{ objectPosition: gravityToObjectPosition(gravity) }} />
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </Sheet>
    );
}

/* ------------------------------------------------------------------ */

export function EditProgressSheet({
    batch: initial,
    confirm,
    onClose,
    onChanged,
}: {
    batch: EditBatch;
    confirm: (spec: ConfirmSpec) => void;
    onClose: () => void;
    onChanged: () => void;
}) {
    const { getEditBatch, rollbackEditBatch } = useMediaEdit();
    const [batch, setBatch] = useState<EditBatch>(initial);
    const [pollFailed, setPollFailed] = useState(false);
    const running = isRunning(batch);
    const changed = useRef(onChanged);
    changed.current = onChanged;

    useEffect(() => {
        if (!running) return;
        const timer = setInterval(async () => {
            const fresh = await getEditBatch(batch.id).catch(() => null);
            if (!fresh) {
                setPollFailed(true);
                return;
            }
            setPollFailed(false);
            setBatch(fresh);
            if (!isRunning(fresh)) changed.current();
        }, 1500);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batch.id, running]);

    const items = batch.items ?? [];
    const done = items.filter((i) => i.status !== 'pending').length;
    const failures = items.filter((i) => i.status === 'failed');

    const undo = () =>
        confirm(
            rollbackSpec(batch, async () => {
                try {
                    const updated = await rollbackEditBatch(batch.id);
                    setBatch((b) => ({ ...b, ...updated }));
                    changed.current();
                } catch (e) {
                    return errText(e, 'Couldn’t roll back — the edit is still in place. Try again.');
                }
            }),
        );

    return (
        <Sheet
            open
            title={`${describeSpec(batch.spec)} — ${statusLabel(batch.status)}`}
            onClose={onClose}
            footer={
                <>
                    {!running && canRollback(batch) && (
                        <button type="button" className="tb-btn danger-soft" onClick={undo}>
                            Undo this edit
                        </button>
                    )}
                    <button type="button" className="tb-new" onClick={onClose}>
                        {running ? 'Keep working' : 'Done'}
                    </button>
                </>
            }
        >
            {running ? (
                <>
                    <div className="progress med-prog" role="progressbar" aria-valuemin={0} aria-valuemax={batch.totalCount} aria-valuenow={done}>
                        <span className="fill" style={{ display: 'block', width: `${batch.totalCount ? Math.round((done / batch.totalCount) * 100) : 0}%` }} />
                    </div>
                    <p>
                        {done.toLocaleString()} of {batch.totalCount.toLocaleString()} processed… Photos stay unchanged until each one finishes. You can close this; the edit
                        keeps running and shows under Edits.
                    </p>
                    {pollFailed && <p className="field-e">Lost touch with the edit — still retrying.</p>}
                </>
            ) : (
                <>
                    <p>
                        {plural(batch.succeededCount, 'item')} edited
                        {batch.failedCount > 0 && `, ${batch.failedCount.toLocaleString()} failed`}
                        {batch.status === 'rolled_back' && ' — rolled back'}
                    </p>
                    {batch.error && <p className="field-e">{batch.error}</p>}
                    {failures.length > 0 && (
                        <div className="med-fails">
                            {failures.map((f) => (
                                <p key={f.id}>
                                    #{f.mediaId}: {f.error}
                                </p>
                            ))}
                        </div>
                    )}
                </>
            )}
        </Sheet>
    );
}

/* ------------------------------------------------------------------ */

export function EditHistorySheet({
    activationId,
    confirm,
    onClose,
    onChanged,
}: {
    activationId: number;
    confirm: (spec: ConfirmSpec) => void;
    onClose: () => void;
    onChanged: () => void;
}) {
    const { listEditBatches, rollbackEditBatch } = useMediaEdit();
    const [batches, setBatches] = useState<EditBatch[] | null>(null);
    const [failed, setFailed] = useState(false);

    const refresh = () => {
        setFailed(false);
        return listEditBatches(activationId)
            .then(setBatches)
            .catch(() => setFailed(true));
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    const rollback = (b: EditBatch) =>
        confirm(
            rollbackSpec(b, async () => {
                try {
                    await rollbackEditBatch(b.id);
                } catch (e) {
                    return errText(e, 'Couldn’t roll back — the edit is still in place. Try again.');
                }
                refresh();
                onChanged();
            }),
        );

    return (
        <Sheet
            open
            wide
            title="Edit history"
            onClose={onClose}
            footer={
                <button type="button" className="tb-new" onClick={onClose}>
                    Done
                </button>
            }
        >
            <div className="med-list">
                {failed && (
                    <p className="field-e">
                        Couldn’t load the edit history.{' '}
                        <button type="button" className="tb-btn sm" onClick={refresh}>
                            Retry
                        </button>
                    </p>
                )}
                {batches == null && !failed && <p className="quiet-empty">Loading…</p>}
                {batches?.length === 0 && <p className="quiet-empty">No bulk edits yet.</p>}
                {(batches ?? []).map((b) => (
                    <div key={b.id} className="med-li">
                        <span className="t">
                            <span className="nm">{describeSpec(b.spec)}</span>
                            <span className="sm">
                                {moment(b.createdAt).format('MMM D, h:mm A')} · {b.succeededCount}/{b.totalCount} edited
                                {b.failedCount > 0 && ` · ${b.failedCount} failed`} · {statusLabel(b.status)}
                            </span>
                        </span>
                        {canRollback(b) && (
                            <button type="button" className="tb-btn sm danger-soft" onClick={() => rollback(b)}>
                                Roll back
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </Sheet>
    );
}

/* ------------------------------------------------------------------ */

export function VersionsSheet({
    media,
    confirm,
    onClose,
    onChanged,
}: {
    media: any;
    confirm: (spec: ConfirmSpec) => void;
    onClose: () => void;
    onChanged: () => void;
}) {
    const { listVersions, restoreVersion } = useMediaEdit();
    const [versions, setVersions] = useState<MediaVersion[] | null>(null);
    const [current, setCurrent] = useState<number | null>(null);
    const [failed, setFailed] = useState(false);

    const refresh = () => {
        setFailed(false);
        return listVersions(media.id)
            .then((r) => {
                setVersions(r.versions ?? []);
                setCurrent(r.currentVersion ?? null);
            })
            .catch(() => setFailed(true));
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [media.id]);

    const currentNo = current ?? versions?.[0]?.versionNumber ?? null;

    const restore = (v: MediaVersion) =>
        confirm({
            title: `Restore version ${v.versionNumber}?`,
            body: (
                <p>
                    Version {v.versionNumber} ({v.source}, {moment(v.createdAt).format('MMM D, h:mm A')}) becomes this photo’s file everywhere — the gallery, slideshow
                    and every shared link. The current version stays in history.
                </p>
            ),
            actionLabel: `Restore v${v.versionNumber}`,
            busyLabel: 'Restoring…',
            danger: true,
            run: async () => {
                try {
                    await restoreVersion(media.id, v.versionNumber);
                } catch (e) {
                    return errText(e, 'Couldn’t restore — the current version is unchanged. Try again.');
                }
                refresh();
                onChanged();
            },
        });

    return (
        <Sheet
            open
            title="Version history"
            onClose={onClose}
            footer={
                <button type="button" className="tb-new" onClick={onClose}>
                    Done
                </button>
            }
        >
            <div className="med-list">
                {failed && (
                    <p className="field-e">
                        Couldn’t load versions.{' '}
                        <button type="button" className="tb-btn sm" onClick={refresh}>
                            Retry
                        </button>
                    </p>
                )}
                {versions == null && !failed && <p className="quiet-empty">Loading…</p>}
                {versions?.length === 0 && <p className="quiet-empty">No versions yet — versions are captured the first time a photo is edited or replaced.</p>}
                {(versions ?? []).map((v) => {
                    const isCurrent = v.versionNumber === currentNo;
                    return (
                        <div key={v.id} className="med-li">
                            <img className="med-vth" src={v.thumbnailTinyUri ?? v.thumbnailUri ?? v.originalUri} alt="" />
                            <span className="t">
                                <span className="nm">
                                    v{v.versionNumber}
                                    {isCurrent && ' · current'}
                                    {v.source === 'backfill' && ' · original'}
                                </span>
                                <span className="sm">
                                    {v.source} · {moment(v.createdAt).format('MMM D, h:mm A')}
                                </span>
                            </span>
                            {!isCurrent && (
                                <button type="button" className="tb-btn sm danger-soft" onClick={() => restore(v)}>
                                    Restore
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </Sheet>
    );
}
